import React from "react";
import { Grid } from "@mui/material";
import { uniq } from "lodash";
import { CheckboxBasic } from "components/common/form";
import { useSidebarFilters } from "../../sidebar-filters";
import { FiltersState } from "../state";
import { FilterProps, FilterName } from ".";


const CheckboxWithInverseContent: React.FC<{
  name: FilterName;
  inverseName: FilterName;
  label: string;
  inverseLabel: string;
  checked: boolean;
  checkedInverse: boolean;
  onChange: (inputName: keyof FiltersState, isChecked: boolean) => void;
}> = ({ name, inverseName, label, inverseLabel, checked, checkedInverse, onChange }) => (
  <>
    <CheckboxBasic
      name={name}
      label={label}
      sx={{ ml: -2 }}
      labelSx={{ ml: 0, pl: 1, width: "initial" }}
      labelStyle={{}}
      checked={checked}
      onChangeAction={(_el, { target: { checked: isChecked } }) => onChange(name, isChecked)}
    />
    <CheckboxBasic
      name={inverseName}
      label={inverseLabel}
      sx={{ ml: -2 }}
      labelSx={{ ml: 0, pl: 1, width: "initial" }}
      labelStyle={{}}
      checked={checkedInverse}
      onChangeAction={(
        _ignore,
        { target: { checked: isChecked } }: React.ChangeEvent<HTMLInputElement>,
      ) => onChange(inverseName, isChecked)}
    />
  </>
);


type FilterCheckboxWithInverseType = FilterProps & {
  inverseLabel: string; showForEveryone?: boolean; wrapInGrid?: boolean;}
// shows all falsey values ** NOTE ** you must add the inverse input name
// as yourname_inverse into the sidebar state/types
export const FilterCheckboxWithInverse: React.FC<FilterCheckboxWithInverseType> = (props) => {
  if (!props.showForEveryone && !props.filters.includes(props.name)) return null;
  const inverseInputName = `${props.name}_inverse` as FilterName;
  const filterKey = props.name;
  const { addFilter, removeFilter, filterState } = useSidebarFilters();
  const current = filterState[filterKey];
  // Check if we have a filter value and determine which checkbox should be checked
  const checked = current?.type === "boolean" && current?.value === true;
  const checkedInverse = current?.type === "inverseBoolean" && current?.value === true;

  const checkKey = (inputName: keyof FiltersState, isChecked: boolean) => {
    if (isChecked) {
      addFilter(filterKey, true, inputName === inverseInputName ? "inverseBoolean" : "boolean");
    } else {
      removeFilter(filterKey);
    }
  };
  const content = (
    <CheckboxWithInverseContent
      name={props.name}
      inverseName={inverseInputName}
      label={props.label}
      inverseLabel={props.inverseLabel}
      checked={checked}
      checkedInverse={checkedInverse}
      onChange={checkKey}
    />
  );

  if (props.wrapInGrid === false) return content;

  return (
    <Grid container>
      <Grid size={{ xs: 12 }}>{content}</Grid>
    </Grid>
  );
};


export const FilterCheckbox: React.FC<Omit<FilterProps, "name"> & {showForEveryone?: boolean; name: string; wrapInGrid?: boolean}> = (props) => {
  const { addFilter, removeFilter, filterState } = useSidebarFilters();
  const name = props.name.replace("filter_", "") as FilterName;
  if (!props.showForEveryone && !props.filters.includes(name)) return null;
  const checked = filterState[name]?.value === true;
  const checkbox = (
    <CheckboxBasic
      name={props.name}
      label={props.label}
      sx={{ ml: -2 }}
      labelSx={{ ml: 0, pl: 1, width: "initial" }}
      labelStyle={{}}
      checked={checked}
      onChangeAction={(_el, { target: { checked: isChecked } }) => {
        if (isChecked) {
          addFilter(name, true, "boolean");
        } else {
          removeFilter(name);
        }
      }}
    />
  );

  if (props.wrapInGrid === false) return checkbox;

  return (
    <Grid size={{ xs: 12 }}>
      {checkbox}
    </Grid>
  );
};

// Checkbox that uses a string facet value instead of a boolean
// eg. if checked and facetValue is "rent" it will return
// results that have a value of "rent"

export const FilterFacetCheckbox: React.FC<FilterProps & {
  showForEveryone?: boolean;
  facetValue: string;
  wrapInGrid?: boolean;
}> = (props) => {
  if (!props.showForEveryone && !props.filters.includes(props.name)) return null;
  const nameWithFacet = `${props.name}:${props.facetValue}`;
  const { addFilter, removeFilter, filterState } = useSidebarFilters();
  const filterData = filterState[props.name];
  // eslint-disable-next-line
  const currentValue = Array.isArray(filterData?.value) ? (filterData?.value as string[]) : filterData?.value ? [filterData.value as string] : [];
  const wrapInGrid = props.wrapInGrid ?? true;

  const checkbox = (
    <CheckboxBasic
      name={nameWithFacet}
      label={props.label}
      sx={{ ml: -2 }}
      labelSx={{ ml: 0, pl: 1, width: "initial" }}
      labelStyle={{}}
      checked={currentValue.includes(props.facetValue)}
      onChangeAction={(_el, { target: { checked: isChecked } }) => {
        if (isChecked) {
          const updatedValue = uniq([...currentValue, props.facetValue]);
          addFilter(props.name, updatedValue, "array");
        } else {
          const updated = currentValue.filter((val) => val !== props.facetValue);
          if (updated.length) {
            addFilter(props.name, updated, "array");
          } else {
            removeFilter(props.name);
          }
        }
      }}
    />
  );

  if (wrapInGrid === false) return checkbox;

  return (
    <Grid size={{ xs: 12 }}>
      {checkbox}
    </Grid>
  );
};
